import type {
  APIBodyParamsV2,
  ApiDashboardElement,
  IntervalJSON,
  SelectionSchemaMeasurePoint,
  SourceMap
} from '@highimpact-frontend/api-types';
import { tr_function } from '@highimpact-frontend/api-types';
import type { DecimationPolicy } from '@highimpact/react-hooks';
import { buildPlotProjectParametersFromContextNames, cloneJsonSerializable } from '@highimpact/react-hooks';
import type { PlotPreferences, ThresholdRequestContextPrefs } from '@highimpact/scatterplot';
import { buildThresholdDataQueryParameters } from '@highimpact/thresholds';

import { buildMeasureRequestSignature } from '../dashboard/widget/widgets/scatterPlot/measureRequestSignature';
import type { DashboardScope } from '../state/dashboardRuntimeTypes';
import { resolveElementMeasureColumnNames } from './buildElementPlotMetadata';
import {
  getElementMeasureKind,
  supportsElementMeasureRequest,
  type ElementMeasureKind,
  type ElementMeasurePlotType,
} from './elementMeasureSupport';
import {
  buildHistogramMeasurePipeline,
  buildOmbrothermicMeasurePipeline,
  buildWindroseMeasurePipeline,
  OMBROTHERMIC_DEFAULT_DECIMATION,
} from './measurePipelineBuilders';
import {
  aggregatedTableHasAggregatedColumns,
  buildAggregationTableContextIndexToName,
  buildAggregationTableMeasurePipeline,
  buildTableByCampaignMeasurePipeline,
} from './tableMeasurePipelineBuilders';

export { supportsElementMeasureRequest };
export type { ElementMeasureKind, ElementMeasurePlotType };

/** Default POST route for v2 measure queries built from element JSON. */
export const MEASURES_V2_URL = '/api/data-dashboard/measures/v2';

export interface MeasureRequestContext {
  readonly scope: DashboardScope;
  readonly intervalOverride?: IntervalJSON;
  readonly thresholdRequest?: ThresholdRequestContextPrefs;
  readonly plotType?: ElementMeasurePlotType;
  readonly decimation?: DecimationPolicy;
  readonly sourceLabelMap?: SourceMap;
}

/** POST body for python-only v2 measure queries built from element JSON. */
export type PythonOnlyMeasureBody = Extract<
  APIBodyParamsV2,
  { variant: 'python_only' }
>;

/** Deep-clones a measure body so widgets never share mutable pipeline state. */
export function clonePythonOnlyMeasureBody(
  body: PythonOnlyMeasureBody
): PythonOnlyMeasureBody {
  return cloneJsonSerializable(body);
}

function createEmptyMeasureBody(): PythonOnlyMeasureBody {
  return {
    variant: 'python_only',
    contexts: [],
    context_index_to_name: {},
    pipeline: { functions: [] },
    time_range: {
      type: 'fixed_time_range',
      startdate: new Date(0).toISOString(),
      enddate: new Date(0).toISOString(),
    },
  };
}

type MeasurePipeline = PythonOnlyMeasureBody['pipeline'];
type MeasurePipelineFunction = MeasurePipeline['functions'][number];

export interface ElementMeasureRequest {
  readonly url: string;
  readonly body: PythonOnlyMeasureBody;
  readonly signature: string;
}

function isMeasurePointElement(element: ApiDashboardElement): boolean {
  return (
    (element.element_type === 'plot' ||
      element.element_type === 'table' ||
      element.element_type === 'tablebycampaign') &&
    Array.isArray(element.data_source)
  );
}

function getElementIntervals(
  element: ApiDashboardElement
): IntervalJSON[] | undefined {
  const properties = element.properties as { intervals?: IntervalJSON[] };
  return properties?.intervals;
}

function isMeasurePointPath(
  source: unknown
): source is SelectionSchemaMeasurePoint {
  return (
    typeof source === 'object' &&
    source !== null &&
    'measure_point_id' in source &&
    'measure_type_id' in source
  );
}

function resolveBarContextIndexToName(
  element: ApiDashboardElement,
  sourceCount: number,
  sourceLabelMap?: SourceMap
): Record<string, string> {
  const names = resolveElementMeasureColumnNames(
    element,
    sourceLabelMap ?? {}
  ).slice(0, sourceCount);

  if (names.length !== sourceCount) {
    return Object.fromEntries(
      Array.from({ length: sourceCount }, (_, index) => [
        String(index),
        String(index),
      ])
    );
  }

  return Object.fromEntries(
    names.map((name, index) => [String(index), name])
  );
}

function resolveContextIndexToName(
  element: ApiDashboardElement,
  sourceCount: number,
  plotType: ElementMeasurePlotType,
  sourceLabelMap?: SourceMap
): Record<string, string> {
  const plotPrefs = (
    element.properties as { additional_properties?: PlotPreferences }
  )?.additional_properties;

  if (plotPrefs?.curveToSourceIndex) {
    return Object.fromEntries(
      Object.entries(plotPrefs.curveToSourceIndex).map(
        ([curveId, sourceIndex]) => [String(sourceIndex), curveId]
      )
    );
  }

  if (plotType === 'ombrothermic') {
    const names = resolveElementMeasureColumnNames(
      element,
      sourceLabelMap ?? {}
    ).slice(0, sourceCount);

    if (names.length === sourceCount) {
      return Object.fromEntries(
        names.map((name, index) => [String(index), name])
      );
    }
  }

  return Object.fromEntries(
    Array.from({ length: sourceCount }, (_, index) => [
      String(index),
      String(index),
    ])
  );
}

function resolveTimeRange(
  element: ApiDashboardElement,
  intervalOverride?: IntervalJSON
): APIBodyParamsV2['time_range'] {
  if (intervalOverride) {
    return intervalOverride as APIBodyParamsV2['time_range'];
  }

  const intervals = getElementIntervals(element);
  if (intervals && intervals.length > 0) {
    return intervals[0] as APIBodyParamsV2['time_range'];
  }

  const props = element.properties as
    | { start_date?: string; end_date?: string }
    | undefined;
  if (props?.start_date && props?.end_date) {
    return {
      type: 'fixed_time_range',
      startdate: props.start_date,
      enddate: props.end_date,
    } as APIBodyParamsV2['time_range'];
  }

  throw new Error('Element has no resolvable time range for measure request');
}

function buildPlotMeasurePipeline(
  contextIndexToName: Record<string, string>,
  _sourceCount: number,
  context: MeasureRequestContext,
  sources: Array<{ measure_point_id: number; measure_type_id: number }>
): MeasurePipeline {
  const useDecimation = context.decimation?.enabled ?? false;
  const projectParams = buildPlotProjectParametersFromContextNames(
    contextIndexToName,
    useDecimation
  );

  const functions: MeasurePipelineFunction[] = [
    tr_function('sort', {
      criteria: [{ column: 'horodate', order: 'ascending' }],
    }),
    tr_function('project', projectParams),
  ];

  const thresholdRequest = context.thresholdRequest;
  const plotType = context.plotType;
  if (
    thresholdRequest?.selectedThresholdCatalog?.length &&
    plotType &&
    thresholdRequest.selectedThresholdAssignmentIds?.length
  ) {
    const selectedIds = new Set(thresholdRequest.selectedThresholdAssignmentIds);
    const selectedThresholds = thresholdRequest.selectedThresholdCatalog.filter(
      (threshold) => selectedIds.has(threshold.id)
    );

    if (selectedThresholds.length > 0) {
      try {
        functions.push(
          buildThresholdDataQueryParameters({
            selectedThresholds,
            selectedSources: sources,
            plotType,
            options: {
              variableOverrides: thresholdRequest.variableMappingsByThresholdId,
            },
          }) as MeasurePipelineFunction
        );
      } catch {
        // Keep the base pipeline when threshold bindings are incomplete.
      }
    }
  }

  return { functions };
}

function buildMeasurePipelineForElement(
  measureKind: ElementMeasureKind,
  contextIndexToName: Record<string, string>,
  sourceCount: number,
  context: MeasureRequestContext,
  sources: Array<{ measure_point_id: number; measure_type_id: number }>,
  element: ApiDashboardElement
): MeasurePipeline {
  switch (measureKind) {
    case 'timeseries':
    case 'scatter':
      return buildPlotMeasurePipeline(
        contextIndexToName,
        sourceCount,
        context,
        sources
      );
    case 'windrose':
      return buildWindroseMeasurePipeline();
    case 'bar':
      return buildHistogramMeasurePipeline(contextIndexToName);
    case 'ombrothermic':
      return buildOmbrothermicMeasurePipeline(contextIndexToName, false);
    case 'table': {
      const config = (element.properties as { config?: { type?: string } }).config;
      if (config?.type !== 'AggregatedTable') {
        throw new Error('Unsupported table config for measure pipeline');
      }

      return buildAggregationTableMeasurePipeline(
        config as Parameters<typeof buildAggregationTableMeasurePipeline>[0]
      );
    }
    case 'tablebycampaign':
      return buildTableByCampaignMeasurePipeline(contextIndexToName);
  }
}

/** Builds a POST `/measures/v2` body from dashboard element JSON and scope. */
export function buildMeasureRequestFromElement(
  element: ApiDashboardElement,
  context: MeasureRequestContext
): ElementMeasureRequest | undefined {
  const measureKind = getElementMeasureKind(element);
  if (!measureKind || !isMeasurePointElement(element)) {
    return undefined;
  }

  const contexts: SelectionSchemaMeasurePoint[] = [];

  for (const source of element.data_source) {
    if (!isMeasurePointPath(source)) {
      continue;
    }
    contexts.push({
      measure_point_id: source.measure_point_id,
      measure_type_id: source.measure_type_id,
      ...(source.group_index != null ? { group_index: source.group_index } : {}),
    });
  }

  if (contexts.length === 0) {
    return undefined;
  }

  if (measureKind === 'windrose' && contexts.length !== 2) {
    return undefined;
  }

  if (measureKind === 'table') {
    const tableConfig = (
      element.properties as {
        config: Parameters<typeof buildAggregationTableContextIndexToName>[0];
      }
    ).config;
    if (!aggregatedTableHasAggregatedColumns(tableConfig)) {
      return undefined;
    }
  }

  const contextIndexToName =
    measureKind === 'table'
      ? buildAggregationTableContextIndexToName(
          (element.properties as { config: Parameters<typeof buildAggregationTableContextIndexToName>[0] })
            .config
        )
      : measureKind === 'bar'
        ? resolveBarContextIndexToName(
            element,
            contexts.length,
            context.sourceLabelMap
          )
        : resolveContextIndexToName(
            element,
            contexts.length,
            measureKind === 'tablebycampaign' ? 'bar' : measureKind,
            context.sourceLabelMap
          );

  if (
    measureKind === 'table' &&
    Object.keys(contextIndexToName).length === 0
  ) {
    return undefined;
  }

  const pipelineContext: MeasureRequestContext = {
    ...context,
    plotType:
      context.plotType ??
      (measureKind === 'timeseries' || measureKind === 'scatter'
        ? measureKind
        : undefined),
  };

  const body: PythonOnlyMeasureBody = {
    variant: 'python_only',
    contexts,
    context_index_to_name: contextIndexToName,
    pipeline: buildMeasurePipelineForElement(
      measureKind,
      contextIndexToName,
      contexts.length,
      pipelineContext,
      contexts,
      element
    ),
    time_range: resolveTimeRange(element, context.intervalOverride),
  };

  if (measureKind === 'tablebycampaign') {
    body.decimation = OMBROTHERMIC_DEFAULT_DECIMATION;
  }

  const signatureIntervals =
    measureKind === 'table' && context.intervalOverride
      ? [context.intervalOverride]
      : getElementIntervals(element) ?? [];

  const signature = buildMeasureRequestSignature({
    intervals: signatureIntervals,
    dataSources: element.data_source,
    measures: contexts,
    thresholdRequest: context.thresholdRequest,
    decimation: context.decimation,
    contextIndexToName,
    pipeline: body.pipeline,
  });

  return {
    url: MEASURES_V2_URL,
    body: clonePythonOnlyMeasureBody(body),
    signature,
  };
}

export { createEmptyMeasureBody };
